import { routePrefixLanguages, languageDutch } from "../i18n";

/**
 * Converts a pathname to include the appropriate language prefix based on the target language.
 *
 * This function removes any existing 2-letter language code from the beginning of the pathname
 * and then constructs a new path with the specified language prefix. For Dutch ("nl"),
 * no language prefix is added to the path.
 *
 * @param pathname - The current pathname that may contain a language prefix (e.g., "/en/about" or "/about")
 * @param lang - The target language code (e.g., "en", "nl", "fr")
 * @returns The pathname with the appropriate language prefix. Returns the path without prefix for "nl",
 *          or with the language code prefixed for other languages (e.g., "/fr/about")
 *
 * @example
 * ```typescript
 * getSlugForLanguage("/en/about", "fr") // Returns "/fr/about"
 * getSlugForLanguage("/en/about", "nl") // Returns "/about"
 * getSlugForLanguage("/about", "en") // Returns "/en/about"
 * ```
 */
export const getSlugForLanguage = (pathname: string, lang: string) => {
  // Remove 2-letter language prefix from path, e.g., "/en/about" → "/about"
  const rawPath = pathname.replace(/^\/[a-z]{2}(?=\/|$)/i, "");

  // Ensure leading slash and construct new path
  const normalizedPath = rawPath.startsWith("/") ? rawPath : `/${rawPath}`;
  return lang === "nl" ? normalizedPath : `/${lang}${normalizedPath}`;
};

/**
 * Creates a clean breadcrumb string from a Storyblok full_slug by removing leading and trailing slashes.
 *
 * This utility function safely handles slug normalization without using regex patterns that could
 * cause performance issues. It uses simple string methods to split, filter, and rejoin path segments.
 *
 * @param fullSlug - The full_slug from Storyblok (e.g., "/news/article/", "news/article", "//news///article//")
 * @returns A clean breadcrumb string without leading/trailing slashes, or empty string if input is falsy
 *
 * @example
 * ```typescript
 * createBreadcrumb("/news/article/") // Returns "news/article"
 * createBreadcrumb("//news///article//") // Returns "news/article"
 * createBreadcrumb("news/article") // Returns "news/article"
 * createBreadcrumb("") // Returns ""
 * createBreadcrumb(null) // Returns ""
 * ```
 */
export const createBreadcrumb = (
  fullSlug: string | null | undefined
): string => {
  if (!fullSlug) {
    return "";
  }

  return fullSlug.split("/").filter(Boolean).join("/");
};

/**
 * Build a localized href for a Storyblok MenuLink item.
 *
 * - For Dutch (nl): use the cached_url as-is and prefix with a single slash.
 * - If the link has an embedded translated_slug for the current language, use that path prefixed by the language.
 * - Else, if a full_slug is available via uuid lookup (by_uuids), use it as-is (it already contains the language).
 * - Else, fallback to the cached_url prefixed by the language.
 *
 * Always returns a string that starts with a single leading slash.
 */
export const buildLocalizedHrefForMenuLink = (
  item: import("../types/NavigationTypes").MenuLink,
  currentLang: string,
  localizedSlugByUuid?: Map<string, string>
): string => {
  const translatedPath = item.link?.story?.translated_slugs?.find(
    (s) => s.lang === currentLang
  )?.path;
  const localizedFullSlug = item.link?.id
    ? localizedSlugByUuid?.get(item.link.id)
    : undefined;
  const cached = item.link?.cached_url ?? "#";

  let slug: string;
  if (currentLang === "nl") {
    slug = cached;
  } else if (translatedPath) {
    slug = `${currentLang}/${translatedPath}`;
  } else if (localizedFullSlug) {
    slug = localizedFullSlug;
  } else {
    slug = `${currentLang}/${cached}`;
  }

  return `/${slug}`;
};

/**
 * Returns the current language based on the pathname prefix.
 * If the first path segment matches one of the supported prefixes (e.g. 'en'),
 * that language is returned; otherwise the default language is returned.
 */
export function getLanguageFromPathname(pathname: string): string {
  const [firstSegment] = pathname.split("/").filter(Boolean);
  if (firstSegment && routePrefixLanguages.includes(firstSegment)) {
    return firstSegment;
  }
  return languageDutch;
}
